import { $, $$, pageData, setLoading } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { fillForm } from '../lib/form.js';
import { mountResourceEditor } from '../lib/resource-editor.js';
import { findSelectMenu, upgradeSelects } from '../lib/select-menu.js';
import { toast } from '../lib/toast.js';

/**
 * The two pages of the Giveaways module.
 *
 * The list carries three immediate actions — end, cancel, delete — which wait
 * for no save bar: they are not settings, they are gestures that draw a winner
 * or destroy something.
 */

interface BonusRole {
  roleId: string;
  entries: number;
}

interface Giveaway {
  _id: string;
  prize: string;
  description: string;
  channelId: string;
  endsAt: string;
  winnerCount: number;
  bonusRoles: BonusRole[];
  requiredRoleIds: string[];
  blockedRoleIds: string[];
  minAccountAgeDays: number;
  minMemberAgeDays: number;
  boostersOnly: boolean;
  dmWinners: boolean;
}

interface EditorData {
  giveaway: Giveaway;
  guildId: string;
}

/* ------------------------------------------------------------------- liste */

export function initGiveawaysListPage(): void {
  const list = $('#giveaway-list');
  if (!list) return;

  const { guildId } = pageData<{ guildId: string }>();

  list.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;

    const actions = [
      { attribute: 'data-end', path: 'end', confirm: 'confirmEndGiveaway', done: 'giveawayEnded' },
      {
        attribute: 'data-cancel',
        path: 'cancel',
        confirm: 'confirmCancelGiveaway',
        done: 'giveawayCancelled',
      },
      {
        attribute: 'data-reroll',
        path: 'reroll',
        confirm: 'confirmRerollGiveaway',
        done: 'giveawayRerolled',
      },
    ] as const;

    for (const action of actions) {
      const button = target.closest<HTMLButtonElement>(`[${action.attribute}]`);
      if (!button) continue;

      const giveawayId = button.getAttribute(action.attribute) ?? '';
      if (!giveawayId || !confirm(ct(action.confirm, 'Are you sure?'))) return;

      void run(button, async () => {
        await api.post(`/api/giveaways/${action.path}?guildId=${guildId}`, {
          guildId,
          giveawayId,
          ...(action.path === 'reroll' ? { count: 1 } : {}),
        });
        toast(ct(action.done, 'Done.'), 'success');
        location.reload();
      });
      return;
    }

    const remove = target.closest<HTMLButtonElement>('[data-remove]');
    if (!remove) return;

    const giveawayId = remove.getAttribute('data-remove') ?? '';
    if (!giveawayId || !confirm(ct('confirmDeleteGiveaway', 'Delete this giveaway?'))) return;

    void run(remove, async () => {
      await api.delete(`/api/giveaways/${giveawayId}?guildId=${guildId}`);
      toast(ct('giveawayDeleted', 'Giveaway deleted.'), 'success');
      location.reload();
    });
  });
}

/** Runs a list action: a loading indicator, errors as a toast. */
async function run(button: HTMLButtonElement, action: () => Promise<void>): Promise<void> {
  setLoading(button, true);
  try {
    await action();
  } catch (error) {
    toast(error instanceof Error ? error.message : String(error), 'error');
  } finally {
    setLoading(button, false);
  }
}

/* ------------------------------------------------------------------ édition */

export function initGiveawaysEditorPage(): void {
  const form = $<HTMLFormElement>('[data-giveaway-form]');
  if (!form) return;

  const { giveaway, guildId } = pageData<EditorData>();

  wireBonusRows(form);
  fillForm(form, giveaway as unknown as Record<string, unknown>);

  // `datetime-local` accepts neither full ISO nor the `Z`: it wants the local
  // minute, with no zone.
  setValue(form, 'endsAt', toLocalInput(giveaway.endsAt));

  rebuildBonuses(form, giveaway.bonusRoles);

  const channel = findSelectMenu(form, 'channelId');
  if (channel) channel.value = giveaway.channelId ? [giveaway.channelId] : [];

  for (const field of ['requiredRoleIds', 'blockedRoleIds'] as const) {
    const menu = findSelectMenu(form, field);
    if (menu) menu.value = giveaway[field];
  }

  for (const flag of ['dmWinners', 'boostersOnly'] as const) {
    const node = form.elements.namedItem(flag);
    if (node instanceof HTMLInputElement) node.checked = giveaway[flag];
  }

  mountResourceEditor({
    endpoint: 'giveaways',
    listUrl: `/modules/giveaways?guildId=${guildId}`,
    guildId,
    form,
    itemId: giveaway._id,

    serialize: () => ({
      prize: text(form, 'prize'),
      description: text(form, 'description'),
      channelId: findSelectMenu(form, 'channelId')?.single ?? '',

      /*
       * Sent back as ISO, and therefore in UTC.
       *
       * The field is entered in the browser's zone: "20:00" means 20:00 to
       * whoever types it. Converting here is what makes the server and the bot
       * talk about the same instant.
       */
      endsAt: new Date(text(form, 'endsAt')).toISOString(),
      winnerCount: number(form, 'winnerCount') || 1,

      bonusRoles: $$<HTMLElement>('[data-bonus-row]', form)
        .map((row) => ({
          roleId: findSelectMenu(row, 'roleId')?.single ?? '',
          entries: Number(fieldValue(row, 'entries')) || 2,
        }))
        .filter((bonus) => bonus.roleId),

      requiredRoleIds: findSelectMenu(form, 'requiredRoleIds')?.value ?? [],
      blockedRoleIds: findSelectMenu(form, 'blockedRoleIds')?.value ?? [],

      minAccountAgeDays: number(form, 'minAccountAgeDays'),
      minMemberAgeDays: number(form, 'minMemberAgeDays'),
      boostersOnly: checked(form, 'boostersOnly'),
      dmWinners: checked(form, 'dmWinners'),
      publish: false,
    }),

    labels: {
      saved: ct('giveawaySaved', 'Giveaway saved.'),
      confirmDelete: ct('confirmDeleteGiveaway', 'Delete this giveaway?'),
      deleted: ct('giveawayDeleted', 'Giveaway deleted.'),
    },
  });
}

/* -------------------------------------------------------------- lignes bonus */

let added = 0;

function wireBonusRows(form: HTMLFormElement): void {
  form.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;

    if (target.closest('[data-add="bonus"]')) {
      addBonus(form);
      return;
    }

    const remove = target.closest('[data-remove]');
    if (remove) {
      remove.closest('[data-bonus-row]')?.remove();
      refreshEmpty(form);
      form.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
}

function addBonus(form: HTMLFormElement): HTMLElement | null {
  const blueprint = $<HTMLTemplateElement>('[data-template="bonus"]');
  const list = $<HTMLElement>('[data-list="bonus"]', form);
  if (!blueprint || !list) return null;

  added += 1;

  const holder = document.createElement('div');
  holder.innerHTML = blueprint.innerHTML.replaceAll('__ROW__', `n${String(added)}`);

  const node = holder.firstElementChild;
  if (!(node instanceof HTMLElement)) return null;

  list.append(node);

  // The row template's role menu is still native: it lived inside a
  // `<template>`, which nothing walked at startup.
  upgradeSelects(document);

  refreshEmpty(form);
  form.dispatchEvent(new Event('change', { bubbles: true }));
  return node;
}

function rebuildBonuses(form: HTMLFormElement, bonuses: BonusRole[]): void {
  const list = $<HTMLElement>('[data-list="bonus"]', form);
  if (!list) return;

  list.replaceChildren();

  for (const bonus of bonuses) {
    const row = addBonus(form);
    if (!row) continue;

    const role = findSelectMenu(row, 'roleId');
    if (role) role.value = bonus.roleId ? [bonus.roleId] : [];

    const entries = row.querySelector('[data-field="entries"]');
    if (entries instanceof HTMLInputElement) entries.value = String(bonus.entries);
  }

  refreshEmpty(form);
}

function refreshEmpty(form: HTMLFormElement): void {
  const note = $<HTMLElement>('[data-empty="bonus"]', form);
  const list = $<HTMLElement>('[data-list="bonus"]', form);
  if (note) note.hidden = (list?.children.length ?? 0) > 0;
}

/* ------------------------------------------------------------------- champs */

/** An ISO date, reduced to what a `datetime-local` can display. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const pad = (value: number) => String(value).padStart(2, '0');
  return (
    `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function text(form: HTMLFormElement, name: string): string {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
    ? node.value.trim()
    : '';
}

function setValue(form: HTMLFormElement, name: string, next: string): void {
  const node = form.elements.namedItem(name);
  if (node instanceof HTMLInputElement) node.value = next;
}

function fieldValue(root: ParentNode, field: string): string {
  const node = root.querySelector(`[data-field="${CSS.escape(field)}"]`);
  return node instanceof HTMLInputElement ? node.value.trim() : '';
}

function number(form: HTMLFormElement, name: string): number {
  const node = form.elements.namedItem(name);
  const value = node instanceof HTMLInputElement ? Number(node.value) : 0;
  return Number.isFinite(value) && value >= 0 ? value : 0;
}

function checked(form: HTMLFormElement, name: string): boolean {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLInputElement ? node.checked : false;
}
